// apps/web/src/features/crm/leads/card/LeadCardHeader.tsx
//
// Header zone of the full-page card (#2308, #2247 FR-2): one signature card
// surface (radius var(--r-4)) with three rows — (1) codes + editable title +
// read-only qualification; (2) client/contact chips + assignee·source·age meta;
// (3) the stage rail + manager side actions. All display fields read from the
// Engagement projection (fidelity variant); every action routes through the
// hoisted legacy stage mutation (`onTransition`). No control renders unless it
// is wired (BR-6): side actions only for a manageable, non-terminal lead.
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  isTerminalEngagementStage,
  type EngagementDetailView,
  type LeadStageCode,
} from '@magimp/shared';
import { Button } from '../../../../ui/index.js';
import { LeadCardScore } from './LeadCardScore.js';
import { LeadCardTitle } from './LeadCardTitle.js';
import { LeadCardHeaderMeta } from './LeadCardHeaderMeta.js';
import { LeadCardClientBar } from './LeadCardClientBar.js';
import { LeadCardStageRail } from './LeadCardStageRail.js';
import { LeadCardSideActions } from './LeadCardSideActions.js';

export interface LeadCardHeaderProps {
  leadId: string;
  detail: EngagementDetailView;
  canManage: boolean;
  canReadClients: boolean;
  /** #2317: admin-or-assigned editability gate for the score control. */
  canEditCard: boolean;
  onTransition: (toStage: LeadStageCode) => void;
  /** Locks the side actions while a stage transition is in flight. */
  busy: boolean;
}

export function LeadCardHeader({
  leadId,
  detail,
  canManage,
  canReadClients,
  canEditCard,
  onTransition,
  busy,
}: LeadCardHeaderProps) {
  const { t } = useTranslation(['leads', 'common']);
  const navigate = useNavigate();
  const terminal = isTerminalEngagementStage(detail.stage);
  const hasClient = detail.clientId !== null;
  const displayTitle = detail.title ?? detail.name ?? t('leads:card.unnamed');

  return (
    <header
      data-testid="lead-card-header"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '16px 18px',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-4)',
        background: 'var(--bg-1)',
      }}
    >
      {/* Row 1 — codes + title + qualification */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Button icon="arrow-left" kind="ghost" size="sm" onClick={() => navigate('/leads')}>
          {t('common:actions.back')}
        </Button>
        <span
          data-testid="lead-card-code"
          style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-3)' }}
        >
          {detail.leadCode}
        </span>
        {detail.dealCode && (
          <span
            data-testid="lead-card-deal-code"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              color: 'var(--status-success-fg)',
            }}
          >
            {detail.dealCode}
          </span>
        )}
        <LeadCardTitle
          leadId={leadId}
          title={displayTitle}
          rawTitle={detail.title}
          canManage={canManage}
        />
        <span style={{ flex: 1 }} />
        {/* #2317 D-1: editable for an editor on a non-terminal (writable) card;
            a terminal/refused/deal card and read-only viewers keep static stars. */}
        <LeadCardScore leadId={leadId} score={detail.score} canEdit={canEditCard && !terminal} />
      </div>

      {/* Row 2 — client/contact chips + meta */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <LeadCardClientBar
          leadId={leadId}
          client={detail.client}
          contactName={detail.name}
          phone={detail.phone ?? null}
          email={detail.email ?? null}
          canManage={canManage}
          canReadClients={canReadClients}
          terminal={terminal}
        />
        <span style={{ flex: 1 }} />
        <LeadCardHeaderMeta
          assigneeName={detail.assigneeName ?? null}
          source={detail.source ?? null}
          createdAt={detail.createdAt}
        />
      </div>

      {/* Row 3 — stage rail + side actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <LeadCardStageRail
          phase={detail.phase}
          stage={detail.stage}
          hasClient={hasClient}
          canManage={canManage}
          onTransition={onTransition}
        />
        <span style={{ flex: 1 }} />
        {canManage && !terminal && <LeadCardSideActions busy={busy} onTransition={onTransition} />}
      </div>
    </header>
  );
}
